import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MessageCircle, ArrowRight } from "lucide-react";
import { Reveal, SectionHead } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  company: "",
  phone: "",
  email: "",
  shipment_type: "Full Truck Load (FTL)",
  origin: "",
  destination: "",
  tonnage: "",
  message: "",
};

const inputCls =
  "w-full bg-[#0D1117] border border-slate-700 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-amber-500 transition-colors duration-200";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mono block mb-2 text-[10px] uppercase tracking-[0.2em] text-slate-500">
      {label}
    </span>
    {children}
  </label>
);

export const QuoteForm = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      toast.success("Quote request received. Our team will reach out shortly.");
      setForm(initial);
    } catch {
      toast.error("Could not send your request. Please email contact@agaman.co.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="quote"
      data-testid="quote-section"
      className="scroll-mt-20 py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[1fr_1.4fr] gap-14 lg:gap-20">
        <div>
          <SectionHead
            overline="Request a Quote"
            title="Tell us the load. We'll move it."
            sub="Share the shipment details and a regional desk will come back with capacity and pricing — typically within four business hours."
          />
          <Reveal delay={0.15} className="mt-10 flex flex-col gap-4">
            <a
              href="mailto:contact@agaman.co"
              data-testid="quote-email-link"
              className="group flex items-center gap-4 border border-slate-800 hover:border-amber-500/60 p-4 transition-colors duration-200"
            >
              <Mail className="w-5 h-5 text-amber-500" />
              <div>
                <p className="mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Email</p>
                <p className="text-sm text-slate-200 group-hover:text-amber-400 transition-colors duration-200">contact@agaman.co</p>
              </div>
            </a>
            <a
              href="tel:+919820045678"
              data-testid="quote-phone-link"
              className="group flex items-center gap-4 border border-slate-800 hover:border-amber-500/60 p-4 transition-colors duration-200"
            >
              <Phone className="w-5 h-5 text-amber-500" />
              <div>
                <p className="mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Phone</p>
                <p className="text-sm text-slate-200 group-hover:text-amber-400 transition-colors duration-200">+91 98200 45678</p>
              </div>
            </a>
            <a
              href="https://wa.me/919820045678"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="quote-whatsapp-link"
              className="group flex items-center gap-4 border border-slate-800 hover:border-amber-500/60 p-4 transition-colors duration-200"
            >
              <MessageCircle className="w-5 h-5 text-amber-500" />
              <div>
                <p className="mono text-[10px] uppercase tracking-[0.2em] text-slate-500">WhatsApp</p>
                <p className="text-sm text-slate-200 group-hover:text-amber-400 transition-colors duration-200">Chat with a regional desk</p>
              </div>
            </a>
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          <form
            onSubmit={submit}
            data-testid="quote-form"
            className="border border-slate-800 bg-[#161B22] p-6 sm:p-9 grid sm:grid-cols-2 gap-5"
          >
            <Field label="Your name *">
              <input data-testid="quote-name-input" required value={form.name} onChange={set("name")} placeholder="Full name" className={inputCls} />
            </Field>
            <Field label="Company">
              <input data-testid="quote-company-input" value={form.company} onChange={set("company")} placeholder="Company name" className={inputCls} />
            </Field>
            <Field label="Phone *">
              <input data-testid="quote-phone-input" required type="tel" value={form.phone} onChange={set("phone")} placeholder="+91 …" className={inputCls} />
            </Field>
            <Field label="Email *">
              <input data-testid="quote-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" className={inputCls} />
            </Field>
            <Field label="Shipment type *">
              <select data-testid="quote-type-select" value={form.shipment_type} onChange={set("shipment_type")} className={inputCls}>
                <option>Full Truck Load (FTL)</option>
                <option>ODC / Project Cargo</option>
                <option>Trailer Transportation</option>
                <option>Warehousing & Storage</option>
                <option>Other</option>
              </select>
            </Field>
            <Field label="Tonnage / volume">
              <input data-testid="quote-tonnage-input" value={form.tonnage} onChange={set("tonnage")} placeholder="e.g. 18 MT / 2,000 sq ft" className={inputCls} />
            </Field>
            <Field label="Origin *">
              <input data-testid="quote-origin-input" required value={form.origin} onChange={set("origin")} placeholder="e.g. Bawal, Haryana" className={inputCls} />
            </Field>
            <Field label="Destination *">
              <input data-testid="quote-destination-input" required value={form.destination} onChange={set("destination")} placeholder="e.g. Chennai, TN" className={inputCls} />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Anything else">
                <textarea
                  data-testid="quote-message-input"
                  rows={4}
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Load dimensions, timelines, handling requirements…"
                  className={`${inputCls} resize-none`}
                />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <button
                type="submit"
                disabled={loading}
                data-testid="quote-submit-btn"
                className="group mono w-full inline-flex items-center justify-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold bg-amber-500 text-[#0D1117] px-7 py-4 hover:bg-amber-400 disabled:opacity-60 transition-colors duration-200"
              >
                {loading ? "Sending…" : "Request Quote"}
                <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
